/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { Department, SubDepartment, Item } from "types/models/menu"
import { actions as menuActions } from "../duck/menu"
import { BulkSelectionsMetadata } from "../duck/menu.type"

const initialState: BulkSelectionsMetadata = {}

const bulkSelectionsV3Slice = createSlice({
  name: "bulkSelectionsV3",
  initialState,
  reducers: {
    /**
     * Select/deselect a department.
     * When selecting: adds to selectedDepartments and clears all child state so children inherit.
     * When deselecting: removes from selectedDepartments and clears all child state.
     */
    multiSelectDepartment(
      state,
      action: PayloadAction<{ department: Department; value: boolean }>,
    ) {
      const { department, value } = action.payload
      const departmentID = department.itemClassID ?? ""

      if (value) {
        if (!state.selectedDepartments) state.selectedDepartments = {}
        state.selectedDepartments[departmentID] = true
      } else if (state.selectedDepartments?.[departmentID]) {
        delete state.selectedDepartments[departmentID]
      }

      // Clear all child state for this department (same logic for select and deselect)
      if (state.excludedSubDepartments) {
        Object.keys(state.excludedSubDepartments).forEach(subDeptID => {
          if (state.excludedSubDepartments![subDeptID] === departmentID) {
            delete state.excludedSubDepartments![subDeptID]
          }
        })
      }
      if (state.excludedItems) {
        Object.keys(state.excludedItems).forEach(itemID => {
          if (state.excludedItems![itemID].deptID === departmentID) {
            delete state.excludedItems![itemID]
          }
        })
      }
      if (state.selectedSubDepartments) {
        Object.keys(state.selectedSubDepartments).forEach(subDeptID => {
          if (state.selectedSubDepartments![subDeptID] === departmentID) {
            delete state.selectedSubDepartments![subDeptID]
          }
        })
      }
      if (state.selectedItems) {
        Object.keys(state.selectedItems).forEach(itemID => {
          if (state.selectedItems![itemID].deptID === departmentID) {
            delete state.selectedItems![itemID]
          }
        })
      }
    },

    /**
     * Select/deselect a subdepartment.
     * When selecting: adds to selectedSubDepartments (or removes from excluded if parent dept is selected).
     * When deselecting: removes from selectedSubDepartments (or adds to excluded if parent dept is selected).
     * Always clears child item state for this subdepartment.
     */
    multiSelectSubDepartment(
      state,
      action: PayloadAction<{ subDepartment: SubDepartment; value: boolean }>,
    ) {
      const { subDepartment, value } = action.payload
      const subDeptID = subDepartment.itemClassID ?? ""
      const parentDeptID = subDepartment.departmentID ?? ""

      const isParentDeptSelected =
        state.selectedDepartments?.[parentDeptID] === true

      if (value) {
        if (isParentDeptSelected) {
          // Parent selected: re-include by removing from excluded list
          if (state.excludedSubDepartments?.[subDeptID]) {
            delete state.excludedSubDepartments[subDeptID]
          }
        } else {
          // Parent not selected: explicitly add to selected list
          if (!state.selectedSubDepartments) state.selectedSubDepartments = {}
          state.selectedSubDepartments[subDeptID] = parentDeptID
        }
        // Clear excluded items for this subdept (selecting subdept selects all its items)
        if (state.excludedItems) {
          Object.keys(state.excludedItems).forEach(itemID => {
            if (state.excludedItems![itemID].subDeptID === subDeptID) {
              delete state.excludedItems![itemID]
            }
          })
        }
        // Clear individually selected items (they inherit from subdept now)
        if (state.selectedItems) {
          Object.keys(state.selectedItems).forEach(itemID => {
            if (state.selectedItems![itemID].subDeptID === subDeptID) {
              delete state.selectedItems![itemID]
            }
          })
        }
      } else if (isParentDeptSelected) {
        // Deselect while parent is selected: add to excluded list
        if (!state.excludedSubDepartments) state.excludedSubDepartments = {}
        state.excludedSubDepartments[subDeptID] = parentDeptID
        // Clear selected items for this subdept (subdept excluded, items can't be selected)
        if (state.selectedItems) {
          Object.keys(state.selectedItems).forEach(itemID => {
            if (state.selectedItems![itemID].subDeptID === subDeptID) {
              delete state.selectedItems![itemID]
            }
          })
        }
      } else {
        // Deselect while parent not selected: remove from selected list
        if (state.selectedSubDepartments?.[subDeptID]) {
          delete state.selectedSubDepartments[subDeptID]
        }
        // Clear individually selected items for this subdept
        if (state.selectedItems) {
          Object.keys(state.selectedItems).forEach(itemID => {
            if (state.selectedItems![itemID].subDeptID === subDeptID) {
              delete state.selectedItems![itemID]
            }
          })
        }
      }
    },

    /**
     * Select/deselect a single item.
     * When selecting: removes from excluded list if parent is selected, otherwise adds to selectedItems.
     * When deselecting: adds to excluded list if parent is selected, otherwise removes from selectedItems.
     */
    multiSelectItem(
      state,
      action: PayloadAction<{ item: Item; value: boolean }>,
    ) {
      const { item, value } = action.payload
      const itemID = item.itemID ?? ""
      const subDeptID = item.subDepartmentID ?? ""
      const deptID = item.departmentID ?? ""

      const isParentDeptSelected = state.selectedDepartments?.[deptID] === true
      const isParentSubDeptSelected =
        state.selectedSubDepartments?.[subDeptID] !== undefined
      const isParentSubDeptExcluded =
        state.excludedSubDepartments?.[subDeptID] !== undefined

      // Parent is effectively selected when:
      // 1. Parent subdept is explicitly selected, OR
      // 2. Parent dept is selected AND parent subdept is NOT excluded
      const isParentSelected =
        isParentSubDeptSelected ||
        (isParentDeptSelected && !isParentSubDeptExcluded)

      if (value) {
        if (isParentSelected) {
          // Parent selected: re-include by removing from excluded list
          if (state.excludedItems?.[itemID]) {
            delete state.excludedItems[itemID]
          }
        } else {
          // Parent not selected: explicitly add to selected list
          // NOTE: subdept stays excluded; this item is an exception
          if (!state.selectedItems) state.selectedItems = {}
          state.selectedItems[itemID] = { subDeptID, deptID }
        }
      } else if (isParentSelected) {
        // Deselect while parent is selected: add to excluded list
        if (!state.excludedItems) state.excludedItems = {}
        state.excludedItems[itemID] = { subDeptID, deptID }
      } else if (state.selectedItems?.[itemID]) {
        // Deselect while parent not selected: remove from selected list
        delete state.selectedItems[itemID]
      }
    },

    /**
     * Set total subdepartments count for a department.
     * Called when subdepartments are loaded (e.g. via RTK Query) to enable accurate checkbox state computation.
     */
    setTotalSubDepartments(
      state,
      action: PayloadAction<{
        departmentID: string
        totalSubDepartments: number
      }>,
    ) {
      const { departmentID, totalSubDepartments } = action.payload
      if (!state.totalSubDepartmentsPerDepartment) {
        state.totalSubDepartmentsPerDepartment = {}
      }
      state.totalSubDepartmentsPerDepartment[departmentID] = totalSubDepartments
    },

    /**
     * Set total items count for a subdepartment.
     * Called when items are loaded (e.g. via RTK Query) to enable accurate checkbox state computation.
     */
    setTotalItems(
      state,
      action: PayloadAction<{ subDepartmentID: string; totalItems: number }>,
    ) {
      const { subDepartmentID, totalItems } = action.payload
      if (!state.totalItemsPerSubDepartment) {
        state.totalItemsPerSubDepartment = {}
      }
      state.totalItemsPerSubDepartment[subDepartmentID] = totalItems
    },

    /**
     * Apply a delta of selection changes made while a filter was active.
     * Called when the filter turns OFF (ON → OFF transition) to write back
     * the user's changes from itemSelections into the authoritative bulkSelectionsV3.
     *
     * Items inside the filter scope that were newly unchecked are added to
     * excludedItems (or removed from selectedItems if no parent is selected).
     * Items inside the filter scope that were newly checked are removed from
     * excludedItems (or added to selectedItems if no parent is selected).
     * Items outside the filter scope are left completely untouched.
     */
    applyFilterSyncDelta(
      state,
      action: PayloadAction<{
        newlyExcluded: {
          [itemID: string]: { subDeptID: string; deptID: string }
        }
        newlySelected: {
          [itemID: string]: { subDeptID: string; deptID: string }
        }
      }>,
    ) {
      const { newlyExcluded, newlySelected } = action.payload

      // Items the user unchecked while filter was active
      Object.entries(newlyExcluded).forEach(
        ([itemID, { subDeptID, deptID }]) => {
          const isParentDeptSelected =
            state.selectedDepartments?.[deptID] === true
          const isParentSubDeptSelected =
            state.selectedSubDepartments?.[subDeptID] !== undefined
          const isParentSubDeptExcluded =
            state.excludedSubDepartments?.[subDeptID] !== undefined
          const isParentSelected =
            isParentSubDeptSelected ||
            (isParentDeptSelected && !isParentSubDeptExcluded)

          if (isParentSelected) {
            // Parent covers this item — add to excluded list
            if (!state.excludedItems) state.excludedItems = {}
            state.excludedItems[itemID] = { subDeptID, deptID }
          } else if (state.selectedItems?.[itemID]) {
            // Item was individually selected — remove it
            delete state.selectedItems[itemID]
          }
        },
      )

      // Items the user checked while filter was active
      Object.entries(newlySelected).forEach(
        ([itemID, { subDeptID, deptID }]) => {
          const isParentDeptSelected =
            state.selectedDepartments?.[deptID] === true
          const isParentSubDeptSelected =
            state.selectedSubDepartments?.[subDeptID] !== undefined
          const isParentSubDeptExcluded =
            state.excludedSubDepartments?.[subDeptID] !== undefined
          const isParentSelected =
            isParentSubDeptSelected ||
            (isParentDeptSelected && !isParentSubDeptExcluded)

          if (isParentSelected) {
            // Parent covers this item — remove from excluded list (re-include)
            if (state.excludedItems?.[itemID]) {
              delete state.excludedItems[itemID]
            }
          } else {
            // No parent — add to individually selected list
            if (!state.selectedItems) state.selectedItems = {}
            state.selectedItems[itemID] = { subDeptID, deptID }
          }
        },
      )
    },

    reset() {
      return initialState
    },
  },
  extraReducers: builder => {
    // MenuEditor calls clearMenu() when switching routes (e.g. menu ↔ inventory).
    // Drawer state is reset there, but bulkSelectionsV3 lives in a separate slice.
    builder.addCase(menuActions.CLEAR_MENU, () => initialState)
  },
})

export const bulkSelectionsV3Actions = bulkSelectionsV3Slice.actions
export const bulkSelectionsV3Reducer = bulkSelectionsV3Slice.reducer
